import { useState } from "react";
import { PanelShell, Badge, TextField, tokens } from "../index";
import { createGatedCampaign, type DJSEMGatedRewardKind, type DJSEMGatedTaskKind } from "@djsem/analysis-contract";

const REWARDS: DJSEMGatedRewardKind[] = ["exclusive_download", "private_track", "sample_pack", "remix_stems", "private_playlist", "early_access", "guestlist_entry", "merch_giveaway"];
const TASKS: DJSEMGatedTaskKind[] = ["follow_spotify", "follow_soundcloud", "follow_instagram", "follow_tiktok", "subscribe_youtube", "join_email_list", "pre_save", "share_link", "submit_remix", "refer_friend"];

/**
 * Gated Drops module — an interactive campaign builder for reward-gated drops: pick a reward
 * and unlock tasks, and see the resulting task list with its (always honestly clamped)
 * verification mode, plus any blockers/warnings from the real V3 Pillar-9 contract.
 *
 * @returns A gated-drop builder panel with reward/task pickers, generated tasks, and any
 *   blockers or warnings.
 */
export function GatedDropsModule() {
  const [title, setTitle] = useState("Unreleased VIP mix drop");
  const [reward, setReward] = useState<DJSEMGatedRewardKind>("exclusive_download");
  const [taskKinds, setTaskKinds] = useState<DJSEMGatedTaskKind[]>(["follow_spotify", "join_email_list"]);
  const toggleTask = (k: DJSEMGatedTaskKind) =>
    setTaskKinds((cur) => (cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]));
  const result = createGatedCampaign({
    title, rewardKind: reward,
    tasks: taskKinds.map((kind) => ({ kind })),
    winnerSelection: "all_qualify",
  });
  return (
    <>
      <PanelShell title="Gated drop builder — verification is always honest">
        <TextField value={title} onChange={setTitle} style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", fontSize: 13, marginBottom: 12 }} />
        <div style={{ fontSize: 11, color: tokens.color.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Reward</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
          {REWARDS.map((r) => (
            <button key={r} onClick={() => setReward(r)} style={{
              padding: "5px 12px", fontSize: 12, cursor: "pointer", borderRadius: 4,
              background: reward === r ? tokens.color.accent : "transparent",
              color: reward === r ? "#12100a" : tokens.color.textDim,
              border: `1px solid ${reward === r ? tokens.color.accent : tokens.color.border}`,
              fontWeight: reward === r ? 600 : 400,
            }}>{r.replace(/_/g, " ")}</button>
          ))}
        </div>
        <div style={{ fontSize: 11, color: tokens.color.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Unlock tasks</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
          {TASKS.map((k) => (
            <button key={k} onClick={() => toggleTask(k)} style={{
              padding: "5px 12px", fontSize: 12, cursor: "pointer", borderRadius: 4,
              background: taskKinds.includes(k) ? tokens.color.accent : "transparent",
              color: taskKinds.includes(k) ? "#12100a" : tokens.color.textDim,
              border: `1px solid ${taskKinds.includes(k) ? tokens.color.accent : tokens.color.border}`,
              fontWeight: taskKinds.includes(k) ? 600 : 400,
            }}>{k.replace(/_/g, " ")}</button>
          ))}
        </div>
        {result.campaign.tasks.map((t) => (
          <div key={t.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13, color: tokens.color.textDim, padding: "5px 0", borderBottom: `1px solid ${tokens.color.border}` }}>
            <span>{t.label}</span>
            <Badge tone={t.verification === "api_verified" || t.verification === "email_verified" ? "ok" : "accent"}>
              {t.verification.replace(/_/g, " ")}
            </Badge>
          </div>
        ))}
        {result.blockers.map((b, i) => <div key={i} style={{ fontSize: 12, color: tokens.color.danger, marginTop: 6 }}>✕ {b}</div>)}
        {result.warnings.map((w, i) => <div key={i} style={{ fontSize: 12, color: tokens.color.warn, marginTop: 6 }}>△ {w}</div>)}
        <div style={{ fontSize: 11, color: tokens.color.muted, marginTop: 10 }}>
          Verification modes are clamped to what platforms actually allow — follows can only be
          link-click verified; DJSEM never fakes platform confirmation.
        </div>
      </PanelShell>
    </>
  );
}
